import { last } from "lodash";
import { Bar, CalendarMode, CalendarView, Row } from "PFApp/booking/types";
import { RowType } from "PFApp/booking/types/row";
import { ProfileBookingsIndex } from "PFCore/hooks/queries/profile";
import { useCallback, useMemo } from "react";

import { WorkforceCalendarData } from "../booking_overview_context.types";
import { useBookingsOverviewContext } from "../bookings_overview_context";
import { getBookingBars, getEngagementBars, getRoleBars } from "./use_row_bars.utils";

interface UseRowBars {
  bookingsByRowKey: ProfileBookingsIndex;
  rowsByKey: Record<string, Row>;
  rows: Row[];
}
interface UseRowBarsReturn {
  barsByRowKey: Record<string, Bar[][]>;
}

export const useRowBars = ({ bookingsByRowKey, rowsByKey, rows }: UseRowBars): UseRowBarsReturn => {
  const {
    calendarMode,
    calendarData: { view }
  } = useBookingsOverviewContext<WorkforceCalendarData>();

  const getBars = useCallback(
    (row: Row) => {
      const closestParentKey = last(row.parents);
      const parentRow = closestParentKey ? rowsByKey[closestParentKey] : null;
      const { type } = row;
      if (calendarMode === CalendarMode.Projects) {
        switch (type) {
          case RowType.Engagement:
            return getEngagementBars(row);
          case RowType.Role:
            return getRoleBars(row);
          case RowType.Profile:
            return getBookingBars(row, parentRow, bookingsByRowKey[row.key], false);
        }
      } else {
        const isSummary = type === "profile" && view !== CalendarView.BookingsOnly;
        const shouldDisplayBookings =
          type !== RowType.Profile ||
          [CalendarView.BookingsOnly, CalendarView.Bookings, CalendarView.Compressed].includes(view);

        return shouldDisplayBookings
          ? getBookingBars(row, parentRow, bookingsByRowKey[row.key], isSummary)
          : [];
      }
    },
    [calendarMode, bookingsByRowKey, rowsByKey, view]
  );

  const barsByRowKey = useMemo(
    () =>
      rows.reduce((result, current) => {
        const bars = getBars(current);
        return {
          ...result,
          [current.key]: bars
        };
      }, {}),
    [rows, getBars]
  );

  return {
    barsByRowKey
  };
};
